import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	getLatestValidation,
	isValidationPending,
	isValidationTerminal,
	listWritingAssistantJobValidations,
	mergeValidationIntoList,
	pollWritingAssistantJobValidations,
	submitWritingAssistantJobValidation,
} from "../../../../services/writingAssistantApi";

export function useWritingJobValidations(websiteId, jobId, changeCount = 0) {
	const [validations, setValidations] = useState([]);
	const [isLoadingValidations, setIsLoadingValidations] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPolling, setIsPolling] = useState(false);
	const [validationStatusLabel, setValidationStatusLabel] = useState(null);
	const pollAbortRef = useRef(false);

	const fetchValidations = useCallback(async () => {
		if (websiteId == null || jobId == null) {
			setValidations([]);
			return { ok: false, validations: [] };
		}

		const result = await listWritingAssistantJobValidations(websiteId, jobId);
		if (result.ok) {
			setValidations(result.validations);
		}
		return result;
	}, [websiteId, jobId]);

	const pollUntilTerminal = useCallback(
		async (validationId) => {
			if (websiteId == null || jobId == null || validationId == null) return;

			pollAbortRef.current = false;
			setIsPolling(true);
			setValidationStatusLabel(null);

			try {
				const result = await pollWritingAssistantJobValidations(
					websiteId,
					jobId,
					validationId,
					{
						onTick: (validation) => {
							if (pollAbortRef.current) return;
							setValidationStatusLabel(validation?.status_label || null);
							setValidations((prev) => mergeValidationIntoList(prev, validation));
						},
					}
				);

				if (!pollAbortRef.current && result.validation) {
					setValidations((prev) => mergeValidationIntoList(prev, result.validation));
				}

				if (!pollAbortRef.current && !result.ok) {
					toast.error(result.message || "Validation failed");
				}
			} catch {
				if (!pollAbortRef.current) {
					toast.error("Failed to check validation status");
				}
			} finally {
				if (!pollAbortRef.current) {
					setIsPolling(false);
					setValidationStatusLabel(null);
				}
			}
		},
		[websiteId, jobId]
	);

	useEffect(() => {
		pollAbortRef.current = true;

		if (websiteId == null || jobId == null || changeCount <= 0) {
			setValidations([]);
			setIsLoadingValidations(false);
			setIsPolling(false);
			setValidationStatusLabel(null);
			return undefined;
		}

		let cancelled = false;

		const load = async () => {
			setIsLoadingValidations(true);
			const result = await fetchValidations();
			if (cancelled) return;

			setIsLoadingValidations(false);

			const latest = getLatestValidation(result.validations);
			if (latest && isValidationPending(latest.status) && latest.id != null) {
				await pollUntilTerminal(latest.id);
			}
		};

		load();

		return () => {
			cancelled = true;
			pollAbortRef.current = true;
		};
	}, [websiteId, jobId, changeCount, fetchValidations, pollUntilTerminal]);

	const handleVerify = useCallback(async () => {
		if (websiteId == null || jobId == null || isSubmitting || isPolling) return;

		setIsSubmitting(true);

		try {
			const submitResult = await submitWritingAssistantJobValidation(websiteId, jobId);

			if (!submitResult.ok) {
				toast.error(submitResult.message || "Failed to submit validation");
				return;
			}

			toast.success(
				submitResult.message || "Writing Assistant job validation submitted successfully"
			);

			const validationId = submitResult.validation?.id;
			if (submitResult.validation) {
				setValidations((prev) =>
					mergeValidationIntoList(prev, submitResult.validation)
				);
			}

			if (validationId != null && !isValidationTerminal(submitResult.validation?.status)) {
				await pollUntilTerminal(validationId);
			}
		} catch {
			toast.error("Failed to verify live content");
		} finally {
			setIsSubmitting(false);
		}
	}, [websiteId, jobId, isSubmitting, isPolling, pollUntilTerminal]);

	const latestValidation = useMemo(
		() =>
			validations.find((v) => String(v?.status || "").toLowerCase() === "completed") ??
			null,
		[validations]
	);

	return {
		validations,
		latestValidation,
		isLoadingValidations,
		isSubmitting,
		isPolling,
		validationStatusLabel,
		handleVerify,
		refreshValidations: fetchValidations,
	};
}
